import { supabase } from '../lib/supabase';

export const companyAccountsService = {
  // Get all company accounts with aggregated data
  async getAllCompanyAccounts() {
    try {
      // Get all companies with their subscriptions
      const { data: companies, error: companiesError } = await supabase?.from('company_settings')?.select(`
          *,
          company_subscriptions (
            current_tier,
            subscription_status,
            active_employee_count,
            monthly_price_kina,
            next_billing_date,
            billing_period_start,
            billing_period_end
          )
        `)?.order('created_at', { ascending: false });

      if (companiesError) throw companiesError;

      // Get latest compliance reports for each company
      const { data: complianceReports, error: reportsError } = await supabase?.from('compliance_reports')?.select('*')?.order('created_at', { ascending: false });

      if (reportsError) throw reportsError;

      // Get employee counts per company
      const { data: employeeCounts, error: employeeError } = await supabase?.from('employees')?.select('company_id, is_active')?.eq('is_active', true);

      if (employeeError) throw employeeError;

      // Get recent activity logs
      const { data: activityLogs, error: logsError } = await supabase?.from('workforce_audit_logs')?.select('*')?.order('created_at', { ascending: false })?.limit(100);

      if (logsError) throw logsError;

      // Aggregate data for each company
      const accounts = companies?.map(company => {
        const subscription = company?.company_subscriptions?.[0] || null;
        const latestReport = complianceReports?.find(r => r?.id) || null;
        const employeeCount = employeeCounts?.filter(e => e?.company_id === company?.id)?.length || 0;
        const recentActivity = activityLogs?.filter(log => log?.company_id === company?.id)?.[0];

        return {
          id: company?.id,
          companyName: company?.company_name,
          companyLegalName: company?.company_legal_name,
          contactEmail: company?.contact_email,
          contactPhone: company?.contact_phone,
          isActive: company?.is_active,
          // Subscription data
          subscriptionTier: subscription?.current_tier || 'N/A',
          subscriptionStatus: subscription?.subscription_status || 'inactive',
          employeeCount: subscription?.active_employee_count || employeeCount,
          monthlyPriceKina: subscription?.monthly_price_kina || 0,
          nextBillingDate: subscription?.next_billing_date,
          // Compliance data
          complianceScore: latestReport?.overall_compliance_score || 0,
          totalViolations: latestReport?.total_violations || 0,
          criticalViolations: latestReport?.critical_violations || 0,
          // Activity
          lastActivityDate: recentActivity?.created_at || company?.updated_at,
          createdAt: company?.created_at,
          updatedAt: company?.updated_at
        };
      }) || [];

      return accounts;
    } catch (error) {
      console.error('Error fetching company accounts:', error);
      throw error;
    }
  },

  // Get executive summary statistics
  async getExecutiveSummary() {
    try {
      const accounts = await this.getAllCompanyAccounts();

      const totalAccounts = accounts?.length || 0;
      const activeAccounts = accounts?.filter(a => a?.isActive && a?.subscriptionStatus === 'active')?.length || 0;
      const suspendedAccounts = accounts?.filter(a => a?.subscriptionStatus === 'suspended')?.length || 0;
      
      const avgComplianceScore = accounts?.length > 0
        ? accounts?.reduce((sum, a) => sum + (a?.complianceScore || 0), 0) / accounts?.length
        : 0;

      const totalRevenue = accounts
        ?.filter(a => a?.subscriptionStatus === 'active')
        ?.reduce((sum, a) => sum + (a?.monthlyPriceKina || 0), 0) || 0;

      const criticalIssues = accounts
        ?.reduce((sum, a) => sum + (a?.criticalViolations || 0), 0) || 0;

      const billingHealthy = accounts?.filter(a => {
        if (!a?.nextBillingDate) return false;
        const daysUntilBilling = Math.floor(
          (new Date(a?.nextBillingDate) - new Date()) / (1000 * 60 * 60 * 24)
        );
        return daysUntilBilling > 0;
      })?.length || 0;

      return {
        totalAccounts,
        activeAccounts,
        suspendedAccounts,
        avgComplianceScore: Math.round(avgComplianceScore * 10) / 10,
        totalRevenueKina: totalRevenue,
        criticalIssues,
        billingHealthPercentage: totalAccounts > 0 ? Math.round((billingHealthy / totalAccounts) * 100) : 0
      };
    } catch (error) {
      console.error('Error fetching executive summary:', error);
      throw error;
    }
  },

  // Suspend company account
  async suspendAccount(companyId) {
    try {
      const { error } = await supabase?.from('company_subscriptions')?.update({ subscription_status: 'suspended' })?.eq('company_id', companyId);

      if (error) throw error;

      // Log the action
      await supabase?.from('workforce_audit_logs')?.insert({
          company_id: companyId,
          action_type: 'billing_updated',
          entity_type: 'company_subscription',
          performed_by: 'admin@system.com',
          changes: { subscription_status: 'suspended' }
        });

      return { success: true };
    } catch (error) {
      console.error('Error suspending account:', error);
      throw error;
    }
  },

  // Reactivate company account
  async reactivateAccount(companyId) {
    try {
      const { error } = await supabase?.from('company_subscriptions')?.update({ subscription_status: 'active' })?.eq('company_id', companyId);

      if (error) throw error;

      // Log the action
      await supabase?.from('workforce_audit_logs')?.insert({
          company_id: companyId,
          action_type: 'billing_updated',
          entity_type: 'company_subscription',
          performed_by: 'admin@system.com',
          changes: { subscription_status: 'active' }
        });

      return { success: true };
    } catch (error) {
      console.error('Error reactivating account:', error);
      throw error;
    }
  },

  // Update billing amount
  async updateBillingAmount(companyId, newAmount) {
    try {
      const { error } = await supabase?.from('company_subscriptions')?.update({ monthly_price_kina: newAmount })?.eq('company_id', companyId);

      if (error) throw error;

      // Log the action
      await supabase?.from('workforce_audit_logs')?.insert({
          company_id: companyId,
          action_type: 'billing_updated',
          entity_type: 'company_subscription',
          performed_by: 'admin@system.com',
          billing_impact_kina: newAmount,
          changes: { monthly_price_kina: newAmount }
        });

      return { success: true };
    } catch (error) {
      console.error('Error updating billing amount:', error);
      throw error;
    }
  },

  // Get account activity logs
  async getAccountActivity(companyId, limit = 20) {
    try {
      const { data, error } = await supabase?.from('workforce_audit_logs')?.select('*')?.eq('company_id', companyId)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        actionType: log?.action_type,
        entityType: log?.entity_type,
        performedBy: log?.performed_by,
        employeeCountBefore: log?.employee_count_before,
        employeeCountAfter: log?.employee_count_after,
        billingImpactKina: log?.billing_impact_kina,
        changes: log?.changes,
        createdAt: log?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching account activity:', error);
      throw error;
    }
  }
};

export default companyAccountsService;